import RequestDemoButton from '../components/RequestDemoButton/RequestDemoButton';
import MockThumb from '../components/MockThumb/MockThumb';
import { mockAssets } from '../config/mockAssets';
import { stockAssets } from '../config/stockAssets';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './SolutionsPage.module.css';

const benefits = [
  'Manage menus, modifiers, and availability in real time',
  'Run a live order queue with status updates and bulk actions',
  'Track revenue, popular items, and fulfillment trends',
  'Coordinate promotions and game-day events from one dashboard',
  'Control which vendors are live for each event',
  'Route every order to the correct section, row, and seat',
];

function SolutionsPage() {
  usePageTitle('Solutions for Stadiums & Arenas');

  return (
    <>
      <section className={styles.hero}>
        <div className={`container ${styles.heroGrid}`}>
          <div>
            <span className="sectionLabel">Solutions</span>
            <h1 className={styles.heroTitle}>Stadiums &amp; Arenas</h1>
            <p className={styles.heroSubtitle}>
              ONI gives venue teams the platform to launch in-seat ordering, coordinate concessions, and fulfill
              every order with confidence on game day.
            </p>
            <RequestDemoButton size="lg" />
          </div>
          <img
            src={stockAssets.solutionsVenue}
            alt="Interior of Mercedes-Benz Stadium in Atlanta with the halo video board and pinwheel roof above a packed crowd"
            className={styles.heroImage}
            loading="eager"
          />
        </div>
      </section>

      <section className={`section ${styles.section}`}>
        <div className={`container ${styles.grid}`}>
          <div className={styles.visual} aria-hidden="true">
            <div className={styles.dashboard}>
              <div className={styles.sidebar}>
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className={styles.main}>
                <div className={styles.topBar}>
                  <strong>Orders</strong>
                  <span>Live queue</span>
                </div>
                <div className={styles.metrics}>
                  <div><small>Today</small><strong>128</strong></div>
                  <div><small>Revenue</small><strong>$4.2k</strong></div>
                  <div><small>Avg wait</small><strong>11m</strong></div>
                </div>
                <div className={styles.queue}>
                  <div className={styles.orderRow}><span>#1042</span><strong>Preparing</strong></div>
                  <div className={styles.orderRow}><span>#1041</span><strong>Ready</strong></div>
                  <div className={styles.orderRow}><span>#1040</span><strong>Delivered</strong></div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <span className="sectionLabel">For venue operators</span>
            <h2 className="sectionTitle">Run stadium concessions with confidence</h2>
            <p className="sectionSubtitle">
              The ONI admin dashboard gives venue teams the tools to manage vendors, fulfill orders, and understand performance on game day.
            </p>
            <ul className={styles.list}>
              {benefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className={`section ${styles.screenshots}`}>
        <div className="container">
          <span className="sectionLabel">Platform preview</span>
          <h2 className="sectionTitle">See the fan and operator experience</h2>
          <p className="sectionSubtitle">
            From mobile ordering to live fulfillment, ONI connects every side of game-day concessions.
          </p>

          <div className={styles.screenshotGrid}>
            <article className={styles.screenshotCard}>
              <div className={styles.phoneFrame}>
                <div className={styles.phoneTop}>Vendor browse</div>
                <div className={styles.menuItem}>
                  <MockThumb src={mockAssets.vendorStadiumGrill} alt="Stadium Grill" />
                  <div>
                    <strong>Stadium Grill</strong>
                    <p>12 min · Section 112</p>
                  </div>
                </div>
                <div className={styles.menuItem}>
                  <MockThumb src={mockAssets.vendorBayBites} alt="Bay Bites" />
                  <div>
                    <strong>Bay Bites</strong>
                    <p>8 min · Snacks</p>
                  </div>
                </div>
              </div>
              <h3>Fan ordering</h3>
              <p>Fans browse vendors, customize orders, and pay without leaving their seats.</p>
            </article>

            <article className={styles.screenshotCard}>
              <div className={styles.phoneFrame}>
                <div className={styles.phoneTop}>Order tracking</div>
                <div className={styles.trackCard}>
                  <span>Order #1042</span>
                  <strong>Out for delivery</strong>
                  <div className={styles.progress}>
                    <span className={styles.progressFill} />
                  </div>
                  <small>ETA 4 min · Section 112, Row 8, Seat 14</small>
                </div>
              </div>
              <h3>Live delivery status</h3>
              <p>Real-time updates keep fans informed while runners know exactly where to go.</p>
            </article>

            <article className={styles.screenshotCard}>
              <div className={styles.desktopFrame}>
                <div className={styles.desktopHeader}>
                  <span>Analytics</span>
                  <span>Orders</span>
                  <span>Vendors</span>
                </div>
                <div className={styles.chartArea}>
                  <div className={styles.bar} style={{ height: '45%' }} />
                  <div className={styles.bar} style={{ height: '70%' }} />
                  <div className={styles.bar} style={{ height: '55%' }} />
                  <div className={styles.bar} style={{ height: '85%' }} />
                </div>
                <div className={styles.table}>
                  <div><span>Vendor</span><strong>Orders</strong><strong>Rev</strong></div>
                  <div><span>Stadium Grill</span><strong>42</strong><strong>$1.8k</strong></div>
                </div>
              </div>
              <h3>Operator dashboard</h3>
              <p>Track orders, revenue, and vendor performance from one command center.</p>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.bottomCta}>
        <div className={`container ${styles.bottomCtaInner}`}>
          <h2>See ONI in action at your venue</h2>
          <p>Request a demo and we will walk your team through setup, fulfillment, and game-day operations.</p>
          <RequestDemoButton size="lg" />
        </div>
      </section>
    </>
  );
}

export default SolutionsPage;
